/** Settings — devices, ID matching, holidays, rules, history import (spec §4.1 screen 7, §3.4). */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Wifi, WifiOff, Link2, Unlink, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import ReasonDialog from './ReasonDialog';
import { api, employeeName, errorText } from './shared';

const RULE_KEYS = [
  ['grace_late_minutes', 'Grace for late arrival (min)', 'سماحية التأخير (دقيقة)', 'number'],
  ['grace_early_minutes', 'Grace for early leave (min)', 'سماحية الانصراف المبكر (دقيقة)', 'number'],
  ['double_punch_seconds', 'Double-punch window (s)', 'نافذة البصمة المكررة (ثانية)', 'number'],
  ['long_span_minutes', 'Long span without break (min)', 'فترة طويلة بلا استراحة (دقيقة)', 'number'],
  ['off_day_base_two_shift', 'Off-day overtime, two-shift production: hours not counted (min; 300 = total − 5 h)', 'إضافي يوم الراحة للإنتاج بفترتين: دقائق لا تُحتسب (300 = المجموع − 5 س)', 'number'],
  ['off_day_base_driver', 'Off-day overtime, drivers: hours not counted (min; 300 = total − 5 h)', 'إضافي يوم الراحة للسائقين: دقائق لا تُحتسب (300 = المجموع − 5 س)', 'number'],
  ['off_day_base_office', 'Off-day overtime, office: hours not counted (min; 0 = all hours)', 'إضافي يوم الراحة للمكتبيين: دقائق لا تُحتسب (0 = كل الساعات)', 'number'],
  ['off_day_rounding', 'Off-day rounding', 'تقريب يوم الراحة', ['none', 'ceil_hour_from_30']],
  ['month_total_rounding', 'Month total rounding', 'تقريب مجموع الشهر', ['none', 'nearest_hour']],
  ['yearly_overtime_limit_minutes', 'Yearly overtime limit (min, empty = none)', 'حد الإضافي السنوي (دقيقة)', 'number'],
];

function Devices({ token, isRTL }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const [devices, setDevices] = useState([]);
  const [branches, setBranches] = useState([]);
  const [edit, setEdit] = useState(null);
  const load = useCallback(() => {
    api(token).get('/devices').then((r) => setDevices(r.devices)).catch((e) => toast.error(errorText(e)));
    axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/branches/`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => setBranches(r.data.branches || [])).catch(() => setBranches([]));
  }, [token]);
  useEffect(() => { load(); }, [load]);
  const save = async (reason) => {
    const body = { reason };
    ['status', 'name', 'branch_id'].forEach((k) => { if (edit[k] !== undefined && edit[k] !== '') body[k] = edit[k]; });
    try { await api(token).patch(`/devices/${edit.sn}`, body); toast.success(t('Device saved', 'تم حفظ الجهاز')); setEdit(null); load(); }
    catch (e) { toast.error(errorText(e)); }
  };
  return (
    <Card>
      <CardHeader><CardTitle className="text-base">{t('Devices', 'الأجهزة')}</CardTitle></CardHeader>
      <CardContent>
        <p className="text-xs text-slate-500 mb-2">{t('A device seen for the first time is pending: its punches are kept but not counted until approved.',
          'الجهاز الجديد يبقى بانتظار الاعتماد: تُحفظ بصماته ولا تُحتسب حتى يُعتمد.')}</p>
        <Table>
          <TableHeader><TableRow>
            <TableHead>{t('Serial', 'الرقم التسلسلي')}</TableHead><TableHead>{t('Name', 'الاسم')}</TableHead>
            <TableHead>{t('Branch', 'الفرع')}</TableHead><TableHead>{t('Status', 'الحالة')}</TableHead>
            <TableHead>{t('Health', 'الاتصال')}</TableHead><TableHead>{t('Punches in HR', 'البصمات في النظام')}</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {devices.map((d) => (
              <TableRow key={d.sn} data-testid={`device-${d.sn}`}>
                <TableCell className="font-mono text-xs">{d.sn}</TableCell>
                <TableCell>{d.name || d.relay_location || '—'}</TableCell>
                <TableCell>{(isRTL ? d.branch_name_ar || d.branch_name : d.branch_name || d.branch_name_ar) || '—'}</TableCell>
                <TableCell><Badge variant={d.status === 'approved' ? 'default' : d.status === 'pending' ? 'secondary' : 'outline'}>
                  {{ approved: t('Approved', 'معتمد'), pending: t('Pending', 'بانتظار الاعتماد'), disabled: t('Disabled', 'موقوف') }[d.status] || d.status}
                </Badge></TableCell>
                <TableCell>{d.online ? <Wifi className="h-4 w-4 text-emerald-600" /> : <WifiOff className="h-4 w-4 text-slate-400" />}</TableCell>
                <TableCell>{d.stored_punches}{d.relay_punch_count ? <span className="text-xs text-slate-500"> / {t('relay', 'المرحّل')} {d.relay_punch_count}</span> : null}</TableCell>
                <TableCell>
                  <Button size="sm" variant="outline" onClick={() => setEdit({ sn: d.sn, status: d.status, name: d.name || '', branch_id: d.branch_id || '' })}
                    data-testid={`device-edit-${d.sn}`}>{t('Edit', 'تعديل')}</Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
      <ReasonDialog open={!!edit} isRTL={isRTL} title={`${t('Device', 'الجهاز')} ${edit?.sn || ''}`} onCancel={() => setEdit(null)} onConfirm={save}>
        {edit && (
          <div className="grid grid-cols-2 gap-2 text-sm">
            <select className="border rounded-md px-2 py-2" value={edit.status} onChange={(e) => setEdit({ ...edit, status: e.target.value })} data-testid="device-status">
              <option value="pending">{t('Pending', 'بانتظار الاعتماد')}</option>
              <option value="approved">{t('Approved', 'معتمد')}</option>
              <option value="disabled">{t('Disabled', 'موقوف')}</option>
            </select>
            <Input value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} placeholder={t('Name', 'الاسم')} />
            <select className="border rounded-md px-2 py-2 col-span-2" value={edit.branch_id} onChange={(e) => setEdit({ ...edit, branch_id: e.target.value })}>
              <option value="">{t('Branch…', 'الفرع…')}</option>
              {branches.map((b) => <option key={b.id} value={b.id}>{isRTL ? b.name_ar || b.name_en : b.name_en || b.name_ar}</option>)}
            </select>
          </div>
        )}
      </ReasonDialog>
    </Card>
  );
}

function IdMatching({ token, isRTL }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const [data, setData] = useState(null);
  const [people, setPeople] = useState([]);
  const [linking, setLinking] = useState(null);
  const [unlinking, setUnlinking] = useState(null);
  const load = useCallback(() => {
    api(token).get('/id-matching').then(setData).catch((e) => toast.error(errorText(e)));
  }, [token]);
  useEffect(() => { load(); api(token).get('/people', { limit: 500 }).then((r) => setPeople(r.items)).catch(() => {}); }, [load, token]);
  const link = async (reason) => {
    try { await api(token).post('/id-links', { pin_index: linking.pin_index, employee_id: linking.employee_id, reason }); setLinking(null); load(); }
    catch (e) { toast.error(errorText(e)); }
  };
  const unlink = async (reason) => {
    try { await api(token).del(`/id-links/${encodeURIComponent(unlinking.pin_index)}`, { reason }); setUnlinking(null); load(); }
    catch (e) { toast.error(errorText(e)); }
  };
  const refresh = async () => {
    try { const r = await api(token).post('/identity-index/refresh'); toast.success(t(`${r.updated} re-indexed`, `أعيدت فهرسة ${r.updated}`)); load(); }
    catch (e) { toast.error(errorText(e)); }
  };
  return (
    <Card>
      <CardHeader><CardTitle className="text-base flex items-center justify-between">
        <span>{t('Device ID matching', 'مطابقة أرقام الأجهزة')}</span>
        {data && <span className="text-sm font-normal text-slate-500">{t(`${data.matched} matched, ${data.unmatched} unmatched`, `${data.matched} مطابق، ${data.unmatched} غير مطابق`)}</span>}
      </CardTitle></CardHeader>
      <CardContent>
        <p className="text-xs text-slate-500 mb-2">
          {t('IDs are matched to iqama / national ID through a keyed hash; only the last 4 digits are shown.',
            'تتم المطابقة مع رقم الإقامة أو الهوية عبر بصمة مشفرة؛ تظهر آخر 4 أرقام فقط.')}
          <Button size="sm" variant="link" onClick={refresh}>{t('Re-match now', 'إعادة المطابقة الآن')}</Button>
        </p>
        {data?.index?.due > 0 && (
          <p className="text-xs text-amber-700 mb-2" data-testid="identity-index-due">
            {t(`${data.index.due} employee ID(s) added or changed since the last re-match; their punches match after Re-match now.`,
              `${data.index.due} رقم هوية أضيف أو تغير منذ آخر مطابقة؛ تُطابق بصماتهم بعد "إعادة المطابقة الآن".`)}
          </p>
        )}
        {!data && <Loader2 className="h-5 w-5 animate-spin text-slate-400" />}
        <Table>
          <TableHeader><TableRow>
            <TableHead>{t('Device ID', 'رقم الجهاز')}</TableHead><TableHead>{t('Devices', 'الأجهزة')}</TableHead>
            <TableHead>{t('Punches', 'البصمات')}</TableHead><TableHead>{t('Last punch', 'آخر بصمة')}</TableHead>
            <TableHead>{t('Employee', 'الموظف')}</TableHead><TableHead />
          </TableRow></TableHeader>
          <TableBody>
            {(data?.items || []).map((r) => (
              <TableRow key={r.pin_index} className={r.employee ? '' : 'bg-amber-50'}>
                <TableCell className="font-mono">{r.pin_masked || '…'}</TableCell>
                <TableCell className="text-xs">{r.devices.join(', ')}</TableCell>
                <TableCell>{r.punches}</TableCell>
                <TableCell className="text-xs">{r.last_punch}</TableCell>
                <TableCell>{r.employee ? <>{employeeName(r.employee, isRTL)} {r.match === 'manual' && <Badge variant="outline">{t('manual', 'يدوي')}</Badge>}</> : <span className="text-amber-700">{t('unmatched', 'غير مطابق')}</span>}</TableCell>
                <TableCell>
                  {r.match === 'manual' ? (
                    <Button size="sm" variant="ghost" onClick={() => setUnlinking(r)}><Unlink className="h-4 w-4" /></Button>
                  ) : !r.employee && (
                    <Button size="sm" variant="outline" onClick={() => setLinking({ pin_index: r.pin_index, masked: r.pin_masked, employee_id: '' })}
                      data-testid="id-link"><Link2 className="h-4 w-4 me-1" />{t('Link', 'ربط')}</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
      <ReasonDialog open={!!linking} isRTL={isRTL} title={t('Link device ID to employee', 'ربط رقم الجهاز بموظف')} onCancel={() => setLinking(null)}
        onConfirm={link}>
        {linking && (
          <select className="border rounded-md px-2 py-2 w-full text-sm" value={linking.employee_id}
            onChange={(e) => setLinking({ ...linking, employee_id: e.target.value })} data-testid="id-link-employee">
            <option value="">{t('Choose an employee…', 'اختر موظفاً…')}</option>
            {people.map((p) => <option key={p.id} value={p.id}>{employeeName(p, isRTL)}{p.employee_number ? ` (${p.employee_number})` : ''}</option>)}
          </select>
        )}
      </ReasonDialog>
      <ReasonDialog open={!!unlinking} isRTL={isRTL} destructive title={t('Remove manual link', 'إزالة الربط اليدوي')}
        onCancel={() => setUnlinking(null)} onConfirm={unlink} />
    </Card>
  );
}

function Holidays({ token, isRTL }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const [year, setYear] = useState(new Date().getFullYear());
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(null);
  const load = useCallback(() => {
    api(token).get('/holidays', { year }).then((r) => setItems(r.holidays)).catch((e) => toast.error(errorText(e)));
  }, [token, year]);
  useEffect(() => { load(); }, [load]);
  const save = async (reason) => {
    try {
      await api(token).put('/holidays', { holidays: [{ ...draft, base_minutes: Number(draft.base_minutes || 0) }], reason });
      toast.success(t('Holiday saved', 'تم حفظ العطلة')); setDraft(null); load();
    } catch (e) { toast.error(errorText(e)); }
  };
  return (
    <Card>
      <CardHeader><CardTitle className="text-base flex items-center gap-2">
        {t('Holiday calendar', 'تقويم العطل')}
        <Input type="number" value={year} onChange={(e) => setYear(Number(e.target.value))} className="w-24 h-8" />
        <Button size="sm" variant="outline" className="ms-auto" onClick={() => setDraft({ day: '', name: '', name_ar: '', base_minutes: 0, kind: 'holiday' })}
          data-testid="holiday-add"><Plus className="h-4 w-4 me-1" />{t('Add', 'إضافة')}</Button>
      </CardTitle></CardHeader>
      <CardContent>
        <p className="text-xs text-slate-500 mb-2">{t('Every hour worked on a holiday is overtime (base 0) unless a base is set. Mark each Ramadan day as Ramadan: it is a working day on the profile’s reduced base.', 'كل ساعة عمل في العطلة إضافي (الأساس 0) ما لم يُحدَّد أساس. حدّد كل يوم من رمضان كـ«رمضان»: يوم عمل بالأساس المخفض في ملف الدوام.')}</p>
        {items.map((h) => (
          <div key={h.day} className="flex gap-3 text-sm border-b py-1">
            <span className="font-mono">{h.day}</span><span>{isRTL ? h.name_ar || h.name : h.name}</span>
            <span className="text-slate-500 ms-auto">
              {h.kind === 'ramadan' ? t('Ramadan: reduced base from the profile', 'رمضان: أساس مخفض من ملف الدوام')
                : `${t('base', 'الأساس')} ${h.base_minutes} ${t('min', 'د')}`}
            </span>
          </div>
        ))}
        {!items.length && <div className="text-sm text-slate-500">{t('No holidays for this year yet.', 'لا توجد عطل لهذه السنة بعد.')}</div>}
      </CardContent>
      <ReasonDialog open={!!draft} isRTL={isRTL} title={t('Holiday', 'عطلة')} onCancel={() => setDraft(null)} onConfirm={save}>
        {draft && (
          <div className="grid grid-cols-2 gap-2 text-sm">
            <Input type="date" value={draft.day} onChange={(e) => setDraft({ ...draft, day: e.target.value })} data-testid="holiday-day" />
            <select className="border rounded-md px-2 py-1" value={draft.kind} onChange={(e) => setDraft({ ...draft, kind: e.target.value })}
              data-testid="holiday-kind">
              <option value="holiday">{t('Public holiday', 'عطلة رسمية')}</option>
              <option value="ramadan">{t('Ramadan (reduced hours)', 'رمضان (ساعات مخفضة)')}</option>
            </select>
            {draft.kind === 'holiday' ? (
              <Input type="number" min="0" value={draft.base_minutes} onChange={(e) => setDraft({ ...draft, base_minutes: e.target.value })}
                placeholder={t('base minutes', 'دقائق الأساس')} />
            ) : <span className="text-xs text-slate-500 self-center">{t('Base from the schedule profile (6 h)', 'الأساس من ملف الدوام (6 ساعات)')}</span>}
            <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Name (English)" data-testid="holiday-name" />
            <Input value={draft.name_ar} onChange={(e) => setDraft({ ...draft, name_ar: e.target.value })} placeholder="الاسم (عربي)" dir="rtl" />
          </div>
        )}
      </ReasonDialog>
    </Card>
  );
}

function Rules({ token, isRTL }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const [versions, setVersions] = useState([]);
  const [draft, setDraft] = useState(null);
  const load = useCallback(() => {
    api(token).get('/settings').then((r) => setVersions(r.versions)).catch((e) => toast.error(errorText(e)));
  }, [token]);
  useEffect(() => { load(); }, [load]);
  const current = versions[versions.length - 1]?.values || {};
  const save = async (reason) => {
    const values = {};
    RULE_KEYS.forEach(([k, , , kind]) => {
      const v = draft.values[k];
      if (v === undefined || v === '' || String(v) === String(current[k] ?? '')) return;
      values[k] = kind === 'number' ? Number(v) : v;
    });
    if (!Object.keys(values).length) { toast.error(t('Nothing changed', 'لا يوجد تغيير')); return; }
    try { await api(token).put('/settings', { effective_from: draft.effective_from, values, reason }); toast.success(t('Rules saved', 'تم حفظ القواعد')); setDraft(null); load(); }
    catch (e) { toast.error(errorText(e)); }
  };
  return (
    <Card>
      <CardHeader><CardTitle className="text-base flex items-center">
        {t('Rules', 'القواعد')}
        <Button size="sm" variant="outline" className="ms-auto" onClick={() => setDraft({ effective_from: new Date().toISOString().slice(0, 8) + '01', values: {} })}
          data-testid="rules-edit">{t('Change from a date', 'تغيير ابتداءً من تاريخ')}</Button>
      </CardTitle></CardHeader>
      <CardContent className="text-sm space-y-1">
        {RULE_KEYS.map(([k, en, ar]) => (
          <div key={k} className="flex justify-between border-b py-1"><span>{isRTL ? ar : en}</span><span className="font-mono">{String(current[k] ?? '—')}</span></div>
        ))}
        <div className="text-xs text-slate-500 pt-2">{t('Versions', 'الإصدارات')}: {versions.map((v) => v.effective_from).join(', ')}</div>
      </CardContent>
      <ReasonDialog open={!!draft} isRTL={isRTL} title={t('New rules from', 'قواعد جديدة ابتداءً من')} onCancel={() => setDraft(null)} onConfirm={save}>
        {draft && (
          <div className="space-y-2 text-sm max-h-80 overflow-auto">
            <Input type="date" value={draft.effective_from} onChange={(e) => setDraft({ ...draft, effective_from: e.target.value })} />
            {RULE_KEYS.map(([k, en, ar, kind]) => (
              <div key={k} className="flex items-center gap-2">
                <span className="flex-1">{isRTL ? ar : en}</span>
                {Array.isArray(kind) ? (
                  <select className="border rounded-md px-2 py-1" value={draft.values[k] ?? current[k] ?? ''}
                    onChange={(e) => setDraft({ ...draft, values: { ...draft.values, [k]: e.target.value } })}>
                    {kind.map((o) => <option key={o} value={o}>{o}</option>)}
                  </select>
                ) : (
                  <Input type="number" className="w-28 h-8" value={draft.values[k] ?? current[k] ?? ''}
                    onChange={(e) => setDraft({ ...draft, values: { ...draft.values, [k]: e.target.value } })} />
                )}
              </div>
            ))}
          </div>
        )}
      </ReasonDialog>
    </Card>
  );
}

const PAY_KEYS = [
  ['pay_period_start_day', 'Pay period starts on day', 'تبدأ فترة الراتب في يوم'],
  ['pay_rate_days', 'Hourly rate: basic ÷ days (period_days = the days of the period)', 'أجر الساعة: الأساسي ÷ الأيام (period_days = أيام الفترة)'],
  ['pay_hours_per_day', 'Hourly rate: ÷ hours per day', 'أجر الساعة: ÷ ساعات اليوم'],
  ['timesheet_review_days', 'Employee review window (days)', 'مهلة مراجعة الموظف (أيام)'],
  ['off_day_review_from_day', 'Managers review the next period’s off days from day', 'يراجع المديرون أيام راحة الفترة القادمة ابتداءً من يوم'],
];

function PaySettings({ token, isRTL }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const [pay, setPay] = useState(null);
  const [draft, setDraft] = useState(null);
  const load = useCallback(() => {
    api(token).get('/settings').then((r) => setPay(r.pay)).catch((e) => toast.error(errorText(e)));
  }, [token]);
  useEffect(() => { load(); }, [load]);
  const current = pay?.current || {};
  const save = async (reason) => {
    const values = {};
    PAY_KEYS.forEach(([k]) => {
      const v = draft.values[k];
      if (v === undefined || v === '' || String(v) === String(current[k] ?? '')) return;
      values[k] = k === 'pay_rate_days' && v === 'period_days' ? v : Number(v);
    });
    if (!Object.keys(values).length) { toast.error(t('Nothing changed', 'لا يوجد تغيير')); return; }
    try { await api(token).put('/settings', { effective_from: draft.effective_from, values, reason }); toast.success(t('Saved', 'تم الحفظ')); setDraft(null); load(); }
    catch (e) { toast.error(errorText(e)); }
  };
  return (
    <Card data-testid="pay-settings">
      <CardHeader><CardTitle className="text-base flex items-center gap-2">
        {t('Pay period and hourly rate', 'فترة الراتب وأجر الساعة')}
        <Badge variant="outline" className="text-amber-800 border-amber-300 bg-amber-50">{t('to confirm with payroll', 'بانتظار تأكيد الرواتب')}</Badge>
        <Button size="sm" variant="outline" className="ms-auto" onClick={() => setDraft({ effective_from: new Date().toISOString().slice(0, 8) + '01', values: {} })}
          data-testid="pay-settings-edit">{t('Change from a period', 'تغيير ابتداءً من فترة')}</Button>
      </CardTitle></CardHeader>
      <CardContent className="text-sm space-y-1">
        {PAY_KEYS.map(([k, en, ar]) => (
          <div key={k} className="flex justify-between border-b py-1"><span>{isRTL ? ar : en}</span>
            <span className="font-mono">{String(current[k] ?? '—')}{!current.effective_from && <span className="text-xs text-amber-700 ms-1">({t('default from the Excel timesheets', 'افتراضي من كشوف Excel')})</span>}</span></div>
        ))}
        <p className="text-xs text-slate-500 pt-2">{t('Defaults are what the Excel timesheets used (26th–25th; basic ÷ days ÷ 8), except that the days are the days of each period instead of a fixed 31. Payroll has not confirmed them. A change applies from the period labelled with the chosen month.',
          'القيم الافتراضية هي ما استخدمته كشوف Excel (من 26 إلى 25؛ الأساسي ÷ الأيام ÷ 8)، مع احتساب أيام كل فترة بدلاً من 31 ثابتة. لم يؤكدها قسم الرواتب بعد. يسري التغيير من الفترة المسماة بالشهر المختار.')}</p>
        {pay?.versions?.length > 0 && <div className="text-xs text-slate-500">{t('Versions', 'الإصدارات')}: {pay.versions.map((v) => v.effective_from).join(', ')}</div>}
      </CardContent>
      <ReasonDialog open={!!draft} isRTL={isRTL} title={t('Pay settings from the period of', 'إعدادات الراتب ابتداءً من فترة')} onCancel={() => setDraft(null)} onConfirm={save}>
        {draft && (
          <div className="space-y-2 text-sm">
            <Input type="month" value={draft.effective_from.slice(0, 7)} onChange={(e) => setDraft({ ...draft, effective_from: `${e.target.value}-01` })} />
            {PAY_KEYS.map(([k, en, ar]) => (
              <div key={k} className="flex items-center gap-2">
                <span className="flex-1">{isRTL ? ar : en}</span>
                <Input className="w-32 h-8" value={draft.values[k] ?? current[k] ?? ''}
                  onChange={(e) => setDraft({ ...draft, values: { ...draft.values, [k]: e.target.value } })} />
              </div>
            ))}
          </div>
        )}
      </ReasonDialog>
    </Card>
  );
}

const WEEKDAY_NAMES = { en: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  ar: ['الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت', 'الأحد'] };

function PatternCoverage({ token, isRTL }) {
  const t = (en, ar) => (isRTL ? ar : en);
  const [cov, setCov] = useState(null);
  useEffect(() => {
    api(token).get('/work-patterns/coverage').then(setCov).catch((e) => toast.error(errorText(e)));
  }, [token]);
  if (!cov) return null;
  const names = cov.company_weekend.map((d) => WEEKDAY_NAMES[isRTL ? 'ar' : 'en'][d]).join(isRTL ? '، ' : ', ') || t('none', 'لا يوجد');
  return (
    <Card data-testid="pattern-coverage">
      <CardHeader><CardTitle className="text-base">{t('Off days', 'أيام الراحة')}</CardTitle></CardHeader>
      <CardContent className="text-sm space-y-1">
        <div>
          <b className={cov.company_default ? 'text-amber-700' : ''}>{cov.company_default}</b>
          {t(` office employee(s) have no off days set by their manager: the company weekend (${names}) is used for them, labelled "company default, not set by manager".`,
            ` موظف مكتبي لم يحدد مديرهم أيام راحتهم: تُستخدم لهم عطلة الشركة (${names})، مع عبارة «الافتراضي للشركة، لم يحدده المدير».`)}
        </div>
        <div data-testid="pattern-no-roster">
          <b className={cov.production_no_roster ? 'text-amber-700' : ''}>{cov.production_no_roster}</b>
          {t(` of ${cov.production_and_drivers} production and driver employees have no off days set for the pay period ${cov.period.start} – ${cov.period.end}: no off day is assumed for them, so days without punches read "Needs review · off days not set".`,
            ` من ${cov.production_and_drivers} موظف إنتاج وسائق بلا أيام راحة محددة لفترة الراتب ${cov.period.start} – ${cov.period.end}: لا يُفترض لهم يوم راحة، فتظهر الأيام بلا بصمات «يحتاج مراجعة · أيام الراحة غير محددة».`)}
        </div>
        <div className="text-xs text-slate-500">
          {t('The direct manager (for their own team), HR or super admin sets off days in Attendance › Off days: standing days that carry over every month, and changes for one month. Per-manager counts are there too. For a first bulk fill, HR can use the Off Days / Half Days / Work Pattern From columns of Import Employees.',
            'يحدد المدير المباشر (لفريقه) أو الموارد البشرية أو المسؤول الأعلى أيام الراحة في الحضور › أيام الراحة: أيام ثابتة تستمر كل شهر، وتعديلات لشهر واحد. وفيها أيضاً الأعداد لكل مدير. وللتعبئة الأولى يمكن للموارد البشرية استخدام أعمدة الاستيراد.')}
        </div>
      </CardContent>
    </Card>
  );
}

export default function SettingsTab({ token, isRTL }) {
  const t = (en, ar) => (isRTL ? ar : en);
  return (
    <div className="space-y-4 mt-4" data-testid="attendance-settings">
      <Devices token={token} isRTL={isRTL} />
      <IdMatching token={token} isRTL={isRTL} />
      <div className="grid gap-4 lg:grid-cols-2">
        <Holidays token={token} isRTL={isRTL} />
        <Rules token={token} isRTL={isRTL} />
      </div>
      <PaySettings token={token} isRTL={isRTL} />
      <PatternCoverage token={token} isRTL={isRTL} />
      <Card>
        <CardHeader><CardTitle className="text-base">{t('Import history', 'استيراد السجل السابق')}</CardTitle></CardHeader>
        <CardContent className="text-sm text-slate-600">
          {t('Punches already collected by the office relay are sent with its backfill command (node backfill.js); it is safe to repeat. When it finishes, the punch count per device above must equal the relay’s count.',
            'تُرسل البصمات التي جمعها جهاز المرحّل في المكتب بأمر الاستيراد (node backfill.js)، ويمكن تكراره بأمان. عند انتهائه يجب أن يساوي عدد البصمات لكل جهاز أعلاه عدد المرحّل.')}
        </CardContent>
      </Card>
    </div>
  );
}
